import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@phosphor-icons/react';
import {
    type SavedAIDesign,
    type TreeCutSubScenario,
    type SelectedDesignForPricing,
    useOneButtonStore,
} from '../../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../../store/store';
import { STABLE_EMPTY_OBJECT } from '../utils/stableEmpty';
import { saveWishlist } from '../../../../../services/geminiService';
import {
    DashboardAiCatalogListRow,
    type DashboardAiCatalogListRowStatusVariant,
} from './DashboardAiCatalogListRow';
import { DashboardAiDesignsEpcModal } from './DashboardAiDesignsEpcModal';
import type { DashboardAiDesignEpcRow } from './DashboardAiDesignsEpcModal.types';
import type { DesignEpcCell, EpcDisplayPartner } from '../utils/pricing';
import {
    isWishlistTreeCutScenario,
    resolveWishlistedDesignSnapshotUrl,
    wishlistRowTitle,
    wishlistScenarioCardKey,
    wishlistTreeCount,
} from '../WishlistSection';
import { computeSystemKwFromModules } from '../hooks/useDashboardDesignHandlers';

export type DashboardAiSlideWishlistListProps = {
    theme: 'dark' | 'light';
    setSelectedRoofCombinationKey: (roofCombinationKey: string) => void;
    handleApplyLayoutClick?: (layoutKey: string, categoryLabel?: string) => void | Promise<void>;
    handleUseThisLayout: (
        scenarioIndex: number,
        layoutKey?: string,
        treeScenarios?: any[],
        categoryLabel?: string,
    ) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    selectedDesignForPricing: SelectedDesignForPricing | null;
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    pricingSelectionLoading: boolean;
    handlePricingDollarClick: (key: string, details: {
        estProd: number | undefined;
        estModules: number | undefined;
        systemKw: string;
        /** Preferred: raw system size in kW (full precision, not display-rounded). */
        systemSizeKw?: number;
        roofCount: number;
        categoryId: string;
        designId: string;
    }) => void;
    solarUtilityData: unknown;
    ensureMainCardPricingForCard: (
        cardKey: string,
        estProd: number | undefined,
        systemKw: string,
    ) => Promise<unknown | null>;
    getDesignEpcByPartner: (
        cardKey: string,
        estProd?: number,
        systemKw?: string,
    ) => Record<EpcDisplayPartner, DesignEpcCell>;
    getMainCardPartnerCellState: (
        cardKey: string,
        partner: EpcDisplayPartner,
        estProd?: number,
        systemKw?: string,
    ) => { status?: string; errorDetail?: string } | undefined;
    isMainCardEpcLoading: (cardKey: string, estProd?: number, systemKw?: string) => boolean;
};

type ScenarioRow = { design: SavedAIDesign; sub: TreeCutSubScenario };

function wishlistScenarioProductionKwh(sub: TreeCutSubScenario, design: SavedAIDesign): number | undefined {
    if (typeof sub.productionW === 'number' && Number.isFinite(sub.productionW) && sub.productionW > 0) {
        return sub.productionW;
    }
    if (typeof design.productionW === 'number' && Number.isFinite(design.productionW) && design.productionW > 0) {
        return design.productionW;
    }
    return undefined;
}

function wishlistScenarioSystemKw(
    sub: TreeCutSubScenario,
    design: SavedAIDesign,
    modulePowerW: number,
): string {
    const modules =
        typeof sub.modules === 'number' && Number.isFinite(sub.modules) && sub.modules > 0
            ? sub.modules
            : typeof design.modules === 'number' && Number.isFinite(design.modules)
              ? design.modules
              : 0;
    const fromModules = computeSystemKwFromModules(modules, modulePowerW);
    if (fromModules > 0) return String(fromModules);
    const kw =
        typeof sub.systemKw === 'number' && Number.isFinite(sub.systemKw)
            ? sub.systemKw
            : typeof design.systemKw === 'number' && Number.isFinite(design.systemKw)
              ? design.systemKw
              : 0;
    return String(kw);
}

/**
 * Wishlist for the narrow slide panel — card list only, same rail row styling as {@link DashboardAiSlideDesignsList}.
 */
export function DashboardAiSlideWishlistList(props: DashboardAiSlideWishlistListProps) {
    const {
        theme,
        setSelectedRoofCombinationKey,
        handleApplyLayoutClick,
        handleUseThisLayout,
        setIsDashboardOpen,
        selectedDesignForPricing,
        pricingDesignForTrigger,
        pricingSelectionLoading,
        handlePricingDollarClick,
        solarUtilityData,
        ensureMainCardPricingForCard,
        getDesignEpcByPartner,
        getMainCardPartnerCellState,
        isMainCardEpcLoading,
    } = props;
    const [scenarioCards, setScenarioCards] = useState<ScenarioRow[]>([]);
    const [epcModalOpen, setEpcModalOpen] = useState(false);
    const savedAIDesigns = useOneButtonStore((s) => s.savedAIDesigns);
    const activeAIDesignId = useOneButtonStore((s) => s.activeAIDesignId);
    const setActiveAIDesignId = useOneButtonStore((s) => s.setActiveAIDesignId);
    const activeSubScenarioId = useOneButtonStore((s) => s.activeSubScenarioId);
    const setActiveSubScenarioId = useOneButtonStore((s) => s.setActiveSubScenarioId);
    const removeTreeCutSubScenario = useOneButtonStore((s) => s.removeTreeCutSubScenario);
    const activeModule = useViewerStore((s) => s.activeModule);
    const modulePowerW = (activeModule as { power?: number })?.power ?? 0;
    const layoutSnapshotsByKey = useViewerStore(
        (s: any) => (s.layoutSnapshotsByKey ?? STABLE_EMPTY_OBJECT) as Record<string, string | undefined>,
    );
    const aiCategoryLayoutVariants = useViewerStore(
        (s: any) => (s.aiCategoryLayoutVariants ?? STABLE_EMPTY_OBJECT) as Record<string, unknown[]>,
    );
    const layoutMetricsByKey = useViewerStore(
        (s: any) =>
            (s.layoutMetricsByKey ?? STABLE_EMPTY_OBJECT) as Record<
                string,
                { multiplier?: number | null } | undefined
            >,
    );
    const treeScenariosByLayoutKey = useViewerStore(
        (s: any) =>
            (s.aiModuleFillingTreeScenariosByLayoutKey ?? STABLE_EMPTY_OBJECT) as Record<string, any[]>,
    );
    const isLoadingSnapshots = useViewerStore((s: any) => Boolean((s as any).isLoadingSnapshots));

    const fallbackSnapshotUrl = useMemo(() => {
        const directDefault =
            (layoutSnapshotsByKey as any)?.default ?? (layoutSnapshotsByKey as any)?.defaultInitial;
        if (typeof directDefault === 'string' && directDefault.trim()) return directDefault.trim();
        const first = Object.values(layoutSnapshotsByKey || {}).find(
            (v) => typeof v === 'string' && v.trim().length > 0,
        ) as string | undefined;
        if (first) return first.trim();
        for (const variants of Object.values(aiCategoryLayoutVariants || {})) {
            const arr = Array.isArray(variants) ? variants : [];
            for (const variant of arr as any[]) {
                const snap = variant?.snapshotUrl;
                if (typeof snap === 'string' && snap.trim().length > 0) return snap.trim();
            }
        }
        return undefined;
    }, [layoutSnapshotsByKey, aiCategoryLayoutVariants]);

    useEffect(() => {
        setScenarioCards(
            savedAIDesigns.flatMap((design) =>
                (design.treeCutSubScenarios ?? []).map((sub) => ({ design, sub })),
            ),
        );
    }, [savedAIDesigns]);

    const hasRows = useMemo(() => scenarioCards.length > 0, [scenarioCards]);

    const removeFromWishlist = (designId: string, subId: string) => {
        removeTreeCutSubScenario(designId, subId, (newWishlist) => saveWishlist(newWishlist));
    };

    const selectRow = (design: SavedAIDesign, sub: TreeCutSubScenario) => {
        setActiveAIDesignId(design.aiDesignId);
        setActiveSubScenarioId(sub.id);
        const selectionKey =
            design.layoutKey === 'default' ? 'default' : design.categoryId?.trim() || design.layoutKey;
        setSelectedRoofCombinationKey(selectionKey);
    };

    const applyRowInViewer = useCallback(
        (design: SavedAIDesign, sub: TreeCutSubScenario) => {
            useViewerStore.setState({ isAiGeneratedDesign: true });
            setActiveAIDesignId(design.aiDesignId);
            setActiveSubScenarioId(sub.id);
            const selectionKey =
                design.layoutKey === 'default' ? 'default' : design.categoryId?.trim() || design.layoutKey;
            setSelectedRoofCombinationKey(selectionKey);
            setIsDashboardOpen(false);

            const applyLayoutKey = design.layoutKey === 'default' ? 'default' : design.layoutKey;
            const applyCategoryLabel =
                design.layoutKey === 'default' ? undefined : design.categoryId?.trim() || undefined;

            if (!isWishlistTreeCutScenario(sub) && typeof handleApplyLayoutClick === 'function') {
                void handleApplyLayoutClick(applyLayoutKey, applyCategoryLabel, {
                    syncCatalogPanelSelection: false,
                });
                return;
            }

            if (typeof handleUseThisLayout === 'function') {
                void handleUseThisLayout(
                    sub.scenarioIndex,
                    design.layoutKey,
                    design.treeScenarios,
                    design.categoryId,
                );
            }
        },
        [
            handleApplyLayoutClick,
            handleUseThisLayout,
            setActiveAIDesignId,
            setActiveSubScenarioId,
            setSelectedRoofCombinationKey,
            setIsDashboardOpen,
        ],
    );

    const handleOpenEpcModal = useCallback(() => {
        setEpcModalOpen(true);
    }, []);

    const handleCloseEpcModal = useCallback(() => {
        setEpcModalOpen(false);
    }, []);

    const epcModalDesigns = useMemo((): DashboardAiDesignEpcRow[] => {
        const rows: DashboardAiDesignEpcRow[] = [];
        for (const { design, sub } of scenarioCards) {
            const cardKey = wishlistScenarioCardKey(design, sub);
            const estProd = wishlistScenarioProductionKwh(sub, design);
            const systemKw = wishlistScenarioSystemKw(sub, design, modulePowerW);
            if (estProd == null || estProd <= 0) continue;
            if (!Number.isFinite(Number(systemKw)) || Number(systemKw) <= 0) continue;
            rows.push({
                cardKey,
                label: wishlistRowTitle(design, sub),
                snapshotUrl: resolveWishlistedDesignSnapshotUrl(
                    design,
                    layoutSnapshotsByKey,
                    aiCategoryLayoutVariants,
                    fallbackSnapshotUrl,
                    sub,
                    treeScenariosByLayoutKey,
                ),
                estProd,
                systemKw,
                multiplier: layoutMetricsByKey[design.layoutKey]?.multiplier ?? null,
            });
        }
        return rows;
    }, [scenarioCards, layoutSnapshotsByKey, aiCategoryLayoutVariants, fallbackSnapshotUrl, treeScenariosByLayoutKey, modulePowerW, layoutMetricsByKey]);

    const selectedWishlistCardKey = useMemo(() => {
        if (selectedDesignForPricing?.designId) return selectedDesignForPricing.designId;
        const active = scenarioCards.find(
            ({ design, sub }) =>
                design.aiDesignId === activeAIDesignId && sub.id === activeSubScenarioId,
        );
        if (!active) return null;
        return wishlistScenarioCardKey(active.design, active.sub);
    }, [
        selectedDesignForPricing?.designId,
        scenarioCards,
        activeAIDesignId,
        activeSubScenarioId,
    ]);

    return (
        <div className={`obd-ai-slide-wishlist obd-theme-${theme}`}>
            {!hasRows && (
                <p className="obd-ai-slide-tree-cutting__empty-note">
                    No wishlisted scenarios yet. Use the heart on a design or tree scenario in the full dashboard.
                </p>
            )}
            {hasRows && (
                <div className={`obd-ai-catalog-list obd-ai-catalog-list--slide-rail obd-theme-${theme}`} role="list">
                    <div className="obd-ai-catalog-list__headrow">
                        <div className="obd-ai-catalog-list__subhead">Wishlist</div>
                    </div>
                    {scenarioCards.map(({ design, sub }, index) => {
                        const pricingDesignId = `wishlist-slide:${design.aiDesignId}:${sub.id}`;
                        const cardKey = wishlistScenarioCardKey(design, sub);
                        const pricingBusy =
                            pricingSelectionLoading &&
                            (selectedDesignForPricing?.designId === pricingDesignId ||
                                pricingDesignForTrigger?.designId === pricingDesignId);
                        const isSelected = activeAIDesignId === design.aiDesignId && activeSubScenarioId === sub.id;
                        const trees = wishlistTreeCount(sub, design);
                        const summaryLine = `${sub.modules ?? 0} modules · ${sub.roofCount ?? 0} roofs · ${trees} trees`;
                        const productionKwh = wishlistScenarioProductionKwh(sub, design);
                        const prodLine =
                            productionKwh != null && Number.isFinite(productionKwh)
                                ? `${Math.round(productionKwh).toLocaleString()} kWh`
                                : '—';
                        const rowTitle = wishlistRowTitle(design, sub);
                        const statusLabel = isSelected ? 'Active' : 'Ready';
                        const statusVariant: DashboardAiCatalogListRowStatusVariant = isSelected ? 'lime' : 'neutral';
                        const systemKwStr = (() => {
                            const n = Number(wishlistScenarioSystemKw(sub, design, modulePowerW));
                            return Number.isFinite(n) && n > 0 ? n.toFixed(2) : '—';
                        })();
                        const epcBusy = isMainCardEpcLoading(cardKey);
                        return (
                            <DashboardAiCatalogListRow
                                key={`${design.aiDesignId}-${sub.id}`}
                                theme={theme}
                                rowIndex={index}
                                cardKey={cardKey}
                                snapshotUrl={resolveWishlistedDesignSnapshotUrl(
                                    design,
                                    layoutSnapshotsByKey,
                                    aiCategoryLayoutVariants,
                                    fallbackSnapshotUrl,
                                    sub,
                                    treeScenariosByLayoutKey,
                                )}
                                snapshotLoading={isLoadingSnapshots}
                                emptyThumb="sparkle"
                                showThumbIndex={false}
                                label={rowTitle}
                                summaryLine={summaryLine}
                                prodLine={prodLine}
                                statusLabel={statusLabel}
                                statusVariant={statusVariant}
                                systemKw={systemKwStr}
                                isSelected={isSelected}
                                isSimulating={false}
                                pricingBusy={pricingBusy}
                                onSelectForPricing={() => selectRow(design, sub)}
                                onActivateDesign={() => applyRowInViewer(design, sub)}
                                onPricing={() =>
                                    handlePricingDollarClick(design.layoutKey, {
                                        estProd: wishlistScenarioProductionKwh(sub, design),
                                        estModules: sub.modules,
                                        systemKw: wishlistScenarioSystemKw(sub, design, modulePowerW),
                                        systemSizeKw:
                                            Number(wishlistScenarioSystemKw(sub, design, modulePowerW)) || undefined,
                                        roofCount: sub.roofCount,
                                        categoryId: design.categoryId || design.label || 'Wishlist',
                                        designId: pricingDesignId,
                                    })
                                }
                                showEpcAction={Boolean(solarUtilityData)}
                                epcBusy={epcBusy}
                                onOpenEpc={handleOpenEpcModal}
                                onOpen3D={() => applyRowInViewer(design, sub)}
                                extraActions={
                                    <button
                                        type="button"
                                        className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--trash"
                                        title="Remove from wishlist"
                                        aria-label="Remove from wishlist"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFromWishlist(design.aiDesignId, sub.id);
                                        }}
                                    >
                                        <TrashIcon size={16} weight="regular" />
                                    </button>
                                }
                            />
                        );
                    })}
                </div>
            )}
            <DashboardAiDesignsEpcModal
                theme={theme}
                open={epcModalOpen}
                onClose={handleCloseEpcModal}
                designs={epcModalDesigns}
                ensurePricingForCard={ensureMainCardPricingForCard}
                getEpcByPartner={getDesignEpcByPartner}
                getPartnerCellState={getMainCardPartnerCellState}
                selectedCardKey={selectedWishlistCardKey}
            />
        </div>
    );
}
